import React, { useState } from 'react';
import {
  Container,
  Typography,
  Box,
  Tabs,
  Tab,
  Paper,
  Button,
  Alert
} from '@mui/material';
import {
  Sms as SmsIcon,
  Email as EmailIcon,
  WhatsApp as WhatsAppIcon,
  ArrowBack as ArrowBackIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import SmsSettings from './sms/SmsSettings';
import SmsTemplates from './sms/SmsTemplates';
import EmailConfig from './email/EmailConfig';
import EmailTemplates from './email/EmailTemplates';
import WhatsappConfig from './whatsapp/WhatsappConfig';
import WhatsappTemplates from './whatsapp/WhatsappTemplates';
import { useAuth } from '../../../contexts/AuthContext';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`communication-tabpanel-${index}`}
      aria-labelledby={`communication-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const CommunicationSettings: React.FC = () => {
  const { user } = useAuth();
  const [value, setValue] = useState(0);
  const [subTabValue, setSubTabValue] = useState(0);
  const isSuperAdmin = user?.role?.code === 'SUPER_ADMIN';

  const handleChange = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
    setSubTabValue(0); // Reset sub-tab when main tab changes
  };

  const handleSubTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setSubTabValue(newValue);
  };

  return (
      <Container maxWidth="lg">
        <Typography variant="h4" gutterBottom>
          Communication Settings
        </Typography>
        <Typography variant="body1" color="textSecondary" paragraph>
          Configure SMS, Email, and WhatsApp gateways and manage message templates.
        </Typography>
        <Alert severity={isSuperAdmin ? 'info' : 'warning'} sx={{ mb: 2 }}>
          {isSuperAdmin
            ? 'You are editing GLOBAL communication defaults. These settings are inherited by schools unless they define their own override.'
            : 'You are editing SCHOOL communication override settings. These values override SuperAdmin global defaults for your school.'}
        </Alert>

        <Paper sx={{ width: '100%', mb: 2 }}>
          <Box sx={{ display: 'flex', justifyContent: 'flex-start', p: 2 }}>
            <Button
              variant="text"
              startIcon={<ArrowBackIcon />}
              onClick={() => window.history.back()}
            >
              Back
            </Button>
          </Box>
          <Tabs
            value={value}
            onChange={handleChange}
            indicatorColor="primary"
            textColor="primary"
            variant="fullWidth"
            aria-label="communication tabs"
          >
            <Tab icon={<SmsIcon />} label="SMS" />
            <Tab icon={<EmailIcon />} label="Email" />
            <Tab icon={<WhatsAppIcon />} label="WhatsApp" />
          </Tabs>

          {/* SMS Panel */}
          <TabPanel value={value} index={0}>
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
              <Tabs value={subTabValue} onChange={handleSubTabChange} aria-label="sms sub tabs">
                <Tab label="Configuration" />
                <Tab label="Templates" />
              </Tabs>
            </Box>
            <TabPanel value={subTabValue} index={0}>
              <SmsSettings />
            </TabPanel>
            <TabPanel value={subTabValue} index={1}>
              <SmsTemplates />
            </TabPanel>
          </TabPanel>

          {/* Email Panel */}
          <TabPanel value={value} index={1}>
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
              <Tabs value={subTabValue} onChange={handleSubTabChange} aria-label="email sub tabs">
                <Tab label="Server Configuration" />
                <Tab label="Templates" />
              </Tabs>
            </Box>
            <TabPanel value={subTabValue} index={0}>
              <EmailConfig />
            </TabPanel>
            <TabPanel value={subTabValue} index={1}>
              <EmailTemplates />
            </TabPanel>
          </TabPanel>

          {/* WhatsApp Panel */}
          <TabPanel value={value} index={2}>
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
              <Tabs value={subTabValue} onChange={handleSubTabChange} aria-label="whatsapp sub tabs">
                <Tab label="Configuration" />
                <Tab label="Templates" />
              </Tabs>
            </Box>
            <TabPanel value={subTabValue} index={0}>
              <WhatsappConfig />
            </TabPanel>
            <TabPanel value={subTabValue} index={1}>
              <WhatsappTemplates />
            </TabPanel>
          </TabPanel>
        </Paper>
      </Container>
  );
};

export default CommunicationSettings;
